import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listVaultFiles,
  getVaultFile,
  putVaultFile,
  deleteVaultFile,
  moveVaultFile,
  bulkUploadVaultFiles,
  listIngestionJobs,
  retryIngestionJob,
  reparseVaultFile,
  type VaultFileWrite,
  type VaultFileMove,
  type VaultBulkConflictStrategy,
} from './vault-api'
import { useWorkspace } from '@/features/workspace/workspace-provider'

export const vaultKeys = {
  all: ['vault'] as const,
  files: () => [...vaultKeys.all, 'files'] as const,
  file: (path: string) => [...vaultKeys.all, 'file', path] as const,
  ingestionJobs: () => [...vaultKeys.all, 'ingestion-jobs'] as const,
}

export function useVaultFiles() {
  const { workspace } = useWorkspace()
  return useQuery({
    queryKey: [...vaultKeys.files(), workspace.id],
    queryFn: () => listVaultFiles(),
    staleTime: 30_000,
  })
}

export function useVaultFile(path: string | null) {
  const { workspace } = useWorkspace()
  return useQuery({
    queryKey: [...vaultKeys.file(path ?? ''), workspace.id],
    queryFn: () => getVaultFile(path!),
    enabled: !!path,
    staleTime: 10_000,
  })
}

export function usePutVaultFile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: VaultFileWrite) => putVaultFile(body),
    onSuccess: (file) => {
      qc.setQueryData(vaultKeys.file(file.path), file)
      void qc.invalidateQueries({ queryKey: vaultKeys.files() })
    },
  })
}

export function useDeleteVaultFile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (path: string) => deleteVaultFile(path),
    onSuccess: (_data, path) => {
      qc.removeQueries({ queryKey: vaultKeys.file(path) })
      void qc.invalidateQueries({ queryKey: vaultKeys.files() })
    },
  })
}

export function useMoveVaultFile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: VaultFileMove) => moveVaultFile(body),
    onSuccess: (file, { from }) => {
      qc.removeQueries({ queryKey: vaultKeys.file(from) })
      qc.setQueryData(vaultKeys.file(file.path), file)
      void qc.invalidateQueries({ queryKey: vaultKeys.files() })
    },
  })
}

export function useBulkUploadVaultFiles() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (opts: {
      files: File[]
      targetFolder: string
      conflictStrategy: VaultBulkConflictStrategy
    }) => bulkUploadVaultFiles(opts),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vaultKeys.files() })
      void qc.invalidateQueries({ queryKey: vaultKeys.ingestionJobs() })
    },
  })
}

export function useIngestionJobs() {
  const { workspace } = useWorkspace()
  return useQuery({
    queryKey: [...vaultKeys.ingestionJobs(), workspace.id],
    queryFn: () => listIngestionJobs(),
    staleTime: 10_000,
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs ?? []
      return jobs.some((job) => job.status === 'pending' || job.status === 'processing')
        ? 2_000
        : 15_000
    },
  })
}

export function useRetryIngestionJob() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (jobId: string) => retryIngestionJob(jobId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vaultKeys.ingestionJobs() })
      void qc.invalidateQueries({ queryKey: vaultKeys.files() })
    },
  })
}

export function useReparseVaultFile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (path: string) => reparseVaultFile(path),
    onSuccess: (_result, path) => {
      void qc.invalidateQueries({ queryKey: vaultKeys.file(path) })
      void qc.invalidateQueries({ queryKey: vaultKeys.files() })
      void qc.invalidateQueries({ queryKey: vaultKeys.ingestionJobs() })
    },
  })
}
